import {inject, Injectable} from '@angular/core';
import {ElectronService} from '../electron/electron.service';
import {IPC} from '../../constants/shared.constants';
import {Clipboard} from '@capacitor/clipboard';
import {SystemInfoService} from '../system-info/system-info.service';

@Injectable({
    providedIn: 'root'
})
export class ClipboardService {
    private readonly systemInfoService = inject(SystemInfoService);

    public async copyToClipboard(text: string): Promise<void> {
        if (this.systemInfoService.isElectron()) {
            return this.copyToElectronClipboard(text);
        } else {
            return this.copyWithBrowserToClipboard(text);
        }
    }

    public async weblinkCopyToClipboard(textPromise: Promise<string>): Promise<boolean> {
        if (this.systemInfoService.isElectron() || this.systemInfoService.isCapacitor() && (this.systemInfoService.isAndroid() || this.systemInfoService.isIos())) {
            const text = await textPromise;
            if (!text) {
                return false;
            }
            if (this.systemInfoService.isElectron()) {
                return this.weblinkCopyToElectronClipboard(text);
            } else {
                return this.weblinkCopyToMobileOSClipboard(text);
            }
        } else {
            return this.weblinkCopyToWebClipboard(textPromise);
        }
    }

    private async copyWithBrowserToClipboard(text: string): Promise<void> {
        return navigator.clipboard.writeText(text);
    }

    private async copyToElectronClipboard(text: string): Promise<void> {
        const result = await this.weblinkCopyToElectronClipboard(text);

        if (!result) {
            throw (new Error('Cant save to clipboard'));
        }
    }

    private async weblinkCopyToMobileOSClipboard(text: string): Promise<boolean> {
        if (text.length > 0) {
            return Clipboard.write({ url: text })
                .then(() => true);
        }

        return false;
    }

    private async weblinkCopyToElectronClipboard(text: string): Promise<boolean> {
        return (ElectronService.ipcRender ? ElectronService.ipcRender.invoke(IPC.SHARE.CLIPBOARD, text) : false);
    }

    private async weblinkCopyToWebClipboard(textPromise: Promise<string>): Promise<boolean> {
        if (typeof ClipboardItem && navigator.clipboard.write) {
            let addUrlToClipboard;
            try {
                const clipboardItem = new ClipboardItem({ 'text/plain': textPromise });

                await navigator.clipboard.write([clipboardItem]);
                addUrlToClipboard = true;
            } catch (e) {
                console.error(e);
                addUrlToClipboard = false;
            }
            return addUrlToClipboard;
        } else {
            const text = await textPromise;
            if (text.length > 0) {
                await navigator.clipboard.writeText(text);
                return true;
            }
            return false;
        }
    }
}
